/*
PURPOSE: Editorial "For Artists" page (heaviest content page).
USE CASES:
- Public /for-artists route.
DOMAIN BOUNDARIES:
- Thin composition of editorial primitives. ALL copy via useCopy()
  forArtists.* — keys unchanged. strategy.howDescription renders as plain
  text (the prior no-op quote-replace + dangerouslySetInnerHTML were
  removed — the string has no quotes/HTML; this also drops a latent XSS
  surface). The nextSteps "Artist Terms and Conditions" split-link is
  reproduced EXACTLY (only the anchor className changes).
- Data-viz components are editorial: the recent-coral and ecosystem
  visualisations use the shared bloom wrappers (RecentCoralsBloom,
  EcosystemBloom); CollectiveImpactTable is restyled into the editorial
  language. ArtistProfileTools, ArtistPayoutChecker and FAQItem stay as
  page-local chrome.
- CTAs were CoralButton(onClick=scroll) → CtaPill (sync). Scroll ref +
  FOR_ARTIST_PAGE_VIEW preserved. S3 video kept in an editorial frame.
  Drop coral-reef-black.svg + blue/dark-blue waves + navy gradients.
- No imports from design-sandbox/.
*/

import React, { useEffect, useRef } from 'react';
import { useCopy } from '../../../hooks/useCopy';
import FAQItem from '../../../components/FAQItem/faqItem';
import { faqData } from './faqForArtists';
import CollectiveImpactTable from '../../../components/CollectiveImpactTable/CollectiveImpactTable';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import RecentCoralsBloom from '../../../components/RecentCoralsBloom/RecentCoralsBloom';
import EcosystemBloom from '../../../components/EcosystemBloom/EcosystemBloom';
import ArtistPayoutChecker from '../../../components/ArtistPayoutChecker/ArtistPayoutChecker';
import ArtistProfileTools from '../../../components/ArtistProfileTools/ArtistProfileTools';
import { EditorialSection, Eyebrow, DisplayHeading, BodyProse, CtaPill } from '../../../components/editorial';
import PublicPageMeta from '../../../components/MetaHead/PublicPageMeta';
import { getTicketsUrl } from '../../../utils/ticketsUrl';

const ForArtists = () => {
  const copy = useCopy();
  const fa = copy.publicPages.forArtists;
  const artistProfileToolsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    trackEvent(EventName.FOR_ARTIST_PAGE_VIEW, {});
  }, []);

  const scrollToArtistProfileTools = () => {
    if (artistProfileToolsRef.current) {
      setTimeout(() => {
        artistProfileToolsRef.current!.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-ivory">
      <PublicPageMeta
        title="For Artists | wearecoral"
        description="Let the fans who love your music back you every month, providing additional income that sits alongside everything you already do."
        path="/for-artists"
      />
      {/* Full-bleed video hero — the concert footage IS the hero (restored
          from the pre-editorial design). A warm editorial-ink scrim keeps the
          footage cinematic while making the serif headline legible; the
          artist-tool card sits on top, light, for contrast and usability. */}
      <section id="for-artists-hero" className="relative overflow-hidden bg-editorial-ink">
        <video
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => {
            console.error("Video loading error:", e);
          }}
        >
          <source src={`https://wearecoral-public-data-prod.s3.ap-southeast-2.amazonaws.com/HipHopConcert.mov`} type="video/mp4" />
        </video>
        {/* Warm scrim — left-weighted so the headline stays legible while the
            footage still reads through on the right. */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-editorial-ink/95 via-editorial-ink/75 to-editorial-ink/40"
          aria-hidden="true"
        />

        <div
          ref={artistProfileToolsRef}
          className="relative z-10 max-w-screen-xl mx-auto px-6 md:px-12 pt-20 pb-20 md:pt-32 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
        >
          <div className="lg:col-span-6">
            <h1 className="font-cormorant font-normal text-ivory text-editorial-display whitespace-pre-line">
              {fa.hero.title}<br /><span className="whitespace-nowrap">{fa.hero.titleBreak}</span>
            </h1>
            <p className="mt-8 max-w-prose text-editorial-body text-ivory/80">
              {fa.hero.subtitle}
            </p>
          </div>
          <div className="lg:col-span-6">
            {/* ArtistProfileTools brings its own translucent dark-glass card
                (bg-white/5 + backdrop-blur, white text) — designed to float
                on the video. No opaque wrapper, or its white text is lost. */}
            <ArtistProfileTools />
          </div>
        </div>
      </section>

      <EditorialSection background="ivory" id="for-artists-advantages">
        <Eyebrow>{fa.keyAdvantages.sectionTitle}</Eyebrow>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
          <div>
            <h3 className="font-cormorant text-editorial-muted text-2xl md:text-3xl leading-tight">{fa.keyAdvantages.beyondSimple.title}</h3>
            <p className="mt-3 text-editorial-body text-editorial-muted/80">{fa.keyAdvantages.beyondSimple.description}</p>
          </div>
          <div>
            <h3 className="font-cormorant text-editorial-muted text-2xl md:text-3xl leading-tight">{fa.keyAdvantages.focusOnMusic.title}</h3>
            <p className="mt-3 text-editorial-body text-editorial-muted/80">{fa.keyAdvantages.focusOnMusic.description}</p>
          </div>
          <div>
            <h3 className="font-cormorant text-editorial-muted text-2xl md:text-3xl leading-tight">{fa.keyAdvantages.sustainableBacking.title}</h3>
            <p className="mt-3 text-editorial-body text-editorial-muted/80">{fa.keyAdvantages.sustainableBacking.description}</p>
          </div>
        </div>
      </EditorialSection>

      <EditorialSection background="cream" id="for-artists-strategy">
        <div className="max-w-prose">
          <Eyebrow>{fa.strategy.whatTitle}</Eyebrow>
          <BodyProse className="mt-6">
            <p>{fa.strategy.paragraph1}</p>
          </BodyProse>

          <Eyebrow className="mt-12">{fa.strategy.howTitle}</Eyebrow>
          <p className="mt-6 text-editorial-body text-editorial-muted/80 leading-relaxed">
            {fa.strategy.howDescription}
          </p>

          <Eyebrow className="mt-12">{fa.strategy.whyTitle}</Eyebrow>
          <BodyProse className="mt-6">
            <p>{fa.strategy.whyDescription}</p>
          </BodyProse>
        </div>

        <div className="mt-16">
          <RecentCoralsBloom />
        </div>
        <div className="flex justify-center mt-16">
          <CtaPill onClick={scrollToArtistProfileTools}>{fa.cta.createArtistPage}</CtaPill>
        </div>
      </EditorialSection>

      <EditorialSection background="ivory" id="for-artists-ecosystem">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <Eyebrow>{fa.ecosystem.sectionTitle}</Eyebrow>
            <DisplayHeading as="h2" scale="heading" className="mt-8">
              {fa.ecosystem.heading}
            </DisplayHeading>
            <BodyProse className="mt-6">
              <p>{fa.ecosystem.description}</p>
            </BodyProse>
            <a href={getTicketsUrl()} target="_blank" rel="noopener noreferrer" className="mt-6 inline-block text-editorial-accent underline">
              {fa.ecosystem.linkText}
            </a>
          </div>
          <div>
            <EcosystemBloom />
          </div>
        </div>
      </EditorialSection>

      <EditorialSection background="cream" id="for-artists-scale">
        <Eyebrow>{fa.coordinatedScale.sectionTitle}</Eyebrow>
        <BodyProse className="mt-6">
          <p>{fa.coordinatedScale.description}</p>
        </BodyProse>
        <div className="mt-10">
          <CollectiveImpactTable />
        </div>
        <div className="flex justify-center mt-16">
          <CtaPill onClick={scrollToArtistProfileTools}>{fa.cta.createArtistPage}</CtaPill>
        </div>
      </EditorialSection>

      <EditorialSection background="ivory" id="for-artists-next-steps">
        <Eyebrow>{fa.nextSteps.sectionTitle}</Eyebrow>
        <ol className="mt-10 space-y-4 text-editorial-body text-editorial-muted/80 leading-relaxed max-w-prose">
          {fa.nextSteps.steps.map((step, index) => (
            <li key={index} className="flex items-start">
              <span className="bg-coral-orange rounded-full w-2.5 h-2.5 mr-4 mt-2 flex-shrink-0" />
              <span>{step}</span>
            </li>
          ))}
        </ol>
        <p className="mt-8 text-editorial-body text-editorial-muted/80 leading-relaxed max-w-prose">
          {fa.nextSteps.termsText.split('Artist Terms and Conditions')[0]}
          <a href="/artist-terms" rel="noopener noreferrer" className="text-editorial-accent underline">{fa.nextSteps.termsLinkText}</a>
          {fa.nextSteps.termsText.split('Artist Terms and Conditions')[1]}
        </p>
        <div className="mt-12">
          <ArtistPayoutChecker />
        </div>
      </EditorialSection>

      <EditorialSection background="cream" id="for-artists-faq">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          <div>
            <Eyebrow>{fa.support.sectionTitle}</Eyebrow>
            <DisplayHeading as="h2" scale="heading" className="mt-8">
              {fa.support.faqTitle}
            </DisplayHeading>
            <BodyProse className="mt-6">
              <p>{fa.support.faqDescription}</p>
            </BodyProse>
          </div>
          <div className="space-y-2 [&_button_span]:!text-editorial-ink [&_button+div]:!text-editorial-muted/80">
            {faqData.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </EditorialSection>

      <EditorialSection background="ivory" id="for-artists-cta">
        <div className="flex flex-col items-center text-center">
          <DisplayHeading as="h2" scale="heading">
            {fa.finalCta.description}
          </DisplayHeading>
          <div className="mt-10">
            <CtaPill onClick={scrollToArtistProfileTools}>{fa.cta.generateProfile}</CtaPill>
          </div>
        </div>
      </EditorialSection>
    </div>
  );
};

export default ForArtists;
